/* =========================================================
   Split reveal — DermX Tattoo / DermX Beauty

   Solo escritorio (>=750px) lleva animación: pin + scrub y las dos puertas
   salen a izquierda y derecha.
   - Móvil (<=749px): sin pin ni animación. El CSS oculta las puertas y deja
     el texto puente como título con Tattoo y Beauty apilados debajo. El pin
     en móvil daba saltos y se rompía con el scroll táctil.
   - prefers-reduced-motion: estado final, sin pin ni animación.
   ========================================================= */

(function () {
  'use strict';

  /* En el Theme Editor la sección re-inyecta su <script> y este archivo se vuelve
     a ejecutar. Si ya hay un módulo vivo, reutilizamos el suyo (mantiene el
     registro de instancias y no duplica los listeners del editor). */
  if (window.DermxSplitReveal) {
    window.DermxSplitReveal.initAll();
    return;
  }

  var GSAP_VERSION = '3.13.0';
  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/gsap.min.js';
  var SCROLLTRIGGER_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/ScrollTrigger.min.js';

  var DESKTOP = '(min-width: 750px)';
  var MOTION_OK = '(prefers-reduced-motion: no-preference)';
  var MOTION_REDUCED = '(prefers-reduced-motion: reduce)';

  /* Una instancia por section.id: { mm } */
  var instances = new Map();

  /* ---------------------------------------------------------
     Carga de GSAP + ScrollTrigger (una sola vez por página)
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> ScrollTrigger
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  /* Promesa compartida: si otra sección de la home ya pidió GSAP, se reutiliza. */
  function ensureGsap() {
    if (window.dermxGsapReady) return window.dermxGsapReady;

    window.dermxGsapReady = Promise.resolve()
      .then(function () {
        if (!window.gsap) return loadScript(GSAP_URL);
        return null;
      })
      .then(function () {
        if (!window.gsap) throw new Error('[split-reveal] GSAP no disponible');
        if (!window.ScrollTrigger && !isPluginRegistered('ScrollTrigger')) {
          return loadScript(SCROLLTRIGGER_URL);
        }
        return null;
      })
      .then(function () {
        var gsap = window.gsap;
        var ScrollTrigger = window.ScrollTrigger || gsap.core.globals().ScrollTrigger;

        if (!ScrollTrigger) throw new Error('[split-reveal] ScrollTrigger no disponible');
        if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

        // En móvil, mostrar/ocultar la barra de direcciones cambia innerHeight:
        // sin esto ScrollTrigger recalcularía a mitad de pin y daría saltos.
        ScrollTrigger.config({ ignoreMobileResize: true });

        return { gsap: gsap, ScrollTrigger: ScrollTrigger };
      });

    return window.dermxGsapReady;
  }

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */

  var refreshTimer = null;

  function refreshSoon(ScrollTrigger) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      ScrollTrigger.refresh();
    }, 60);
  }

  /* Las imágenes que aún no han cargado cambian la altura del layout: refrescamos. */
  function refreshOnImages(root, ScrollTrigger) {
    root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          refreshSoon(ScrollTrigger);
        },
        { once: true }
      );
    });
  }

  /* ---------------------------------------------------------
     Ciclo de vida por sección
     --------------------------------------------------------- */

  function destroy(sectionId) {
    var instance = instances.get(sectionId);
    if (!instance) return;
    instance.mm.revert(); // mata tweens, sets, pins y ScrollTriggers de esta sección
    instances.delete(sectionId);
  }

  function init(root) {
    if (!root) return;

    var sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    destroy(sectionId); // evita duplicados al re-renderizar en el editor

    ensureGsap()
      .then(function (lib) {
        // El editor puede haber quitado la sección mientras cargaba el CDN.
        if (!root.isConnected) return;
        build(root, sectionId, lib.gsap, lib.ScrollTrigger);
      })
      .catch(function (error) {
        // Sin GSAP mostramos el estado final: la sección sigue siendo usable.
        root.classList.add('split-reveal--static');
        console.warn(error);
      });
  }

  function build(root, sectionId, gsap, ScrollTrigger) {
    var select = gsap.utils.selector(root); // todo scopeado a esta sección
    var doorStart = root.querySelector('[data-split-door="start"]');
    var doorEnd = root.querySelector('[data-split-door="end"]');
    var bridge = root.querySelector('[data-split-bridge]');
    var contents = select('[data-split-panel-content]');
    var media = select('.split-reveal__panel-media');

    var mm = gsap.matchMedia();
    instances.set(sectionId, { mm: mm });

    /* Construye la timeline atada al scroll (solo escritorio). */
    function slidingDoors(options) {
      gsap.set(contents, { autoAlpha: 0, y: 24 });
      if (bridge) gsap.set(bridge, { autoAlpha: 1 });

      var timeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          id: 'split-reveal-' + sectionId + '-' + options.name,
          trigger: root,
          start: 'top top',
          end: function () {
            return '+=' + Math.round(window.innerHeight * (options.distanceVh / 100));
          },
          scrub: 1,
          pin: root,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      // El texto puente se va en cuanto arranca la separación, no espera al final.
      if (bridge) {
        timeline.to(bridge, { autoAlpha: 0, duration: 0.18, ease: 'power1.in' }, 0);
      }

      timeline
        .to(doorStart, { xPercent: -100, duration: 0.85 }, 0)
        .to(doorEnd, { xPercent: 100, duration: 0.85 }, 0)
        // Ligero parallax de las mitades reveladas mientras se abren las puertas.
        .fromTo(media, { scale: 1.06 }, { scale: 1, duration: 0.85 }, 0)
        // Títulos y CTA entran cuando las puertas casi han salido de pantalla.
        .to(contents, { autoAlpha: 1, y: 0, duration: 0.28, stagger: 0.06, ease: 'power2.out' }, 0.62)
        // Pequeña pausa final para que el estado final "respire" antes de despinear.
        .to({}, { duration: 0.12 });

      return function () {
        if (timeline.scrollTrigger) timeline.scrollTrigger.kill(true);
        timeline.kill();
      };
    }

    /* --- Movimiento reducido: estado final, sin pin ni animación --- */
    mm.add(MOTION_REDUCED, function () {
      root.classList.add('split-reveal--static');
      return function () {
        root.classList.remove('split-reveal--static');
      };
    });

    /* --- Escritorio. Móvil no monta nada: lo resuelve el CSS. --- */
    mm.add(DESKTOP + ' and ' + MOTION_OK, function () {
      return slidingDoors({
        name: 'desktop',
        distanceVh: parseFloat(root.dataset.scrollDistance) || 180,
      });
    });

    refreshOnImages(root, ScrollTrigger);
    refreshSoon(ScrollTrigger);
  }

  /* ---------------------------------------------------------
     Arranque + eventos del Theme Editor
     --------------------------------------------------------- */

  function initAll() {
    document.querySelectorAll('.split-reveal[data-section-id]').forEach(init);
  }

  window.DermxSplitReveal = { init: init, initAll: initAll, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', function () {
    if (window.gsap && isPluginRegistered('ScrollTrigger')) {
      refreshSoon(window.gsap.core.globals().ScrollTrigger);
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.split-reveal[data-section-id]');
    if (!root) return;
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('.split-reveal[data-section-id]');
    if (!root) return;
    destroy(root.dataset.sectionId);
  });

  // Al seleccionar/deseleccionar en el editor cambian offsets y alturas.
  ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select'].forEach(function (
    eventName
  ) {
    document.addEventListener(eventName, function () {
      if (window.gsap && isPluginRegistered('ScrollTrigger')) {
        refreshSoon(window.gsap.core.globals().ScrollTrigger);
      }
    });
  });
})();
